import { QueryFunctionContext, useInfiniteQuery, useMutation, useQueries, useQueryClient } from '@tanstack/react-query'
import { AxiosError, AxiosResponse } from 'axios'
import { generatePath } from 'react-router-dom'

import api from '../../api'
import { AccountingKeys, RequestsKeys, OrgStructureKeys } from '../Keys'

import { useGetOne, useCreate, usePatch, useGetList } from '../request'

import {
  ApprovalRequestList,
  ApprovalRequest,
  Filter,
  ExportFilter,
  FiltersList,
  RequestPayload,
  ApproveRejectRequest,
  ApproveRejectResponse,
  RequestStepUpdateRequest,
  Comment,
  BulkApproveRejectRequest,
  ApprovalRequestVersionData,
  CommentRequest,
  PublishRequest,
  AutoDecisionRequest,
} from '@models/Request'

export { RequestsKeys }

const PATHS = {
  requests: '/approval-requests/',
  sendNow: '/approval-requests/:id/send',
  publish: '/approval-requests/:id/publish',
  bulk: '/approval-requests/bulk',
  filters: '/approval-requests/filters',
  fieldValues: '/approval-requests/field-values',
  export: '/approval-requests/export',
  comments: '/approval-requests/:id/comments',
  remind: '/approval-requests/:id/remind',
  autoDecision: '/approval-requests/:id/auto-decision/:decision',
  forward: '/approval-requests/:id/forward',
}

export const useGetRequests = (filter?: Partial<Filter>, enabled = true) => {
  return useGetOne<ApprovalRequestList>([RequestsKeys.Requests, filter], PATHS.requests, {
    query: {
      enabled,
    },
    axios: {
      params: { ...(filter || {}) },
    },
  })
}
export const useInfiniteGetRequestsByStatus = (filter?: Filter) => {
  const onLoadMore = async (ctx: QueryFunctionContext) => {
    const { data }: AxiosResponse = await api.get(PATHS.requests, {
      params: {
        ...filter,
        page: ctx.pageParam || 1,
      },
    })
    return { rows: data?.results, count: data.count }
  }

  const { data, isFetchingNextPage, fetchNextPage, hasNextPage, refetch } = useInfiniteQuery(
    [RequestsKeys.Requests, filter?.status],
    (ctx) => onLoadMore(ctx),
    {
      getNextPageParam: (lastPage, allPages) => {
        return lastPage.count <= (filter?.size || 0) * allPages.length ? null : allPages.length
      },
    },
  )
  return { data, isFetchingNextPage, fetchNextPage, hasNextPage, refetch }
}

export const useGetRequest = (id?: number | string) => {
  return useGetOne<ApprovalRequest>([RequestsKeys.Request, id], `${PATHS.requests}${id}`, {
    query: {
      enabled: !!id,
    },
  })
}

export const useGetRequestsByIds = (ids?: Array<number>) => {
  return useQueries({
    queries:
      ids?.map((id) => {
        return {
          queryKey: [RequestsKeys.Request, id],
          staleTime: Infinity,
          queryFn: async () => (await api.get<ApprovalRequest>(`${PATHS.requests}${id}`)).data,
        }
      }) || [],
  })
}

export const useGetRequestData = (id?: number | string, versionId?: number | string) => {
  return useGetOne<ApprovalRequestVersionData>(
    [RequestsKeys.Request, id, versionId],
    `${PATHS.requests}${id}/data/${versionId}`,
    {
      query: {
        enabled: !!id && !!versionId,
        staleTime: Infinity,
      },
    },
  )
}

// for one time fetching reasons
export const useFetchRequest = () => {
  return useMutation<ApprovalRequest, unknown, number | string>(async (id: number | string) => {
    const res = await api.get<ApprovalRequest>(`${PATHS.requests}${id}`)
    return res.data
  })
}

export const useValidateRequest = (id?: number | string, uuid?: string) => {
  return useGetOne<unknown>([RequestsKeys.RequestValidation, uuid], `${PATHS.requests}${id}/validate/${uuid}`, {
    query: {
      initialData: {},
      enabled: !!id && !!uuid,
    },
  })
}

export const useGetRequestsFilters = () => {
  return useGetOne<FiltersList>([RequestsKeys.Filters], PATHS.filters, {
    query: {
      suspense: true,
    },
  })
}

export const useGetFieldValues = (workflowId?: string | number | null) => {
  return useGetOne<Record<string, { value: string; label: string }[]>>(
    [RequestsKeys.Filters, 'field-values', workflowId],
    PATHS.fieldValues,
    {
      query: {
        enabled: !!workflowId,
      },
      axios: {
        params: { workflow: workflowId },
      },
    },
  )
}

export const useApproveReject = () => {
  const cache = useQueryClient()

  return useMutation<ApproveRejectResponse, AxiosError<ApproveRejectResponse>, ApproveRejectRequest>(
    async ({ status, id, comment, uuid, signature }: ApproveRejectRequest) => {
      const path = `${PATHS.requests}${uuid ? 'p/' : ''}${uuid || id}/${status}`
      const res = await api.patch<ApproveRejectResponse>(path, { comment: comment || null, signature })
      return res.data
    },
    {
      mutationKey: [RequestsKeys.ApproveReject],
      onSuccess: (_, variables) => {
        cache.invalidateQueries([RequestsKeys.Request, variables.id])
        cache.invalidateQueries([RequestsKeys.Requests])
        cache.invalidateQueries([OrgStructureKeys.OrgStructure])
        cache.removeQueries({ queryKey: [AccountingKeys.Contacts], type: 'inactive' })
      },
    },
  )
}

export const useAutoDecision = () => {
  const cache = useQueryClient()

  return useMutation<ApproveRejectResponse, AxiosError<ApproveRejectResponse>, AutoDecisionRequest>(
    async ({ decision, id }: AutoDecisionRequest) => {
      const path = `${PATHS.autoDecision.replace(':id', id).replace(':decision', decision)}`
      const res = await api.patch<ApproveRejectResponse>(path)
      return res.data
    },
    {
      mutationKey: [RequestsKeys.ApproveReject],
      onSuccess: (_, variables) => {
        cache.invalidateQueries([RequestsKeys.Request, variables.id])
        cache.invalidateQueries([RequestsKeys.Requests])
        cache.invalidateQueries([OrgStructureKeys.OrgStructure])
        cache.removeQueries({ queryKey: [AccountingKeys.Contacts], type: 'inactive' })
      },
    },
  )
}

export const usePublishRequest = () => {
  const cache = useQueryClient()

  return useMutation<ApproveRejectResponse, AxiosError<ApproveRejectResponse>, PublishRequest>(
    async ({ id }: PublishRequest) => {
      const res = await api.patch<ApproveRejectResponse>(PATHS.publish.replace(':id', id?.toString() || ''))
      return res.data
    },
    {
      mutationKey: [RequestsKeys.PublishRequest],
      onSuccess: (_, variables) => {
        cache.invalidateQueries([RequestsKeys.Request, variables.id])
        cache.invalidateQueries([RequestsKeys.Requests])
      },
    },
  )
}

export const useBulkApproveReject = () => {
  const cache = useQueryClient()
  return useMutation<ApproveRejectResponse[], AxiosError<BulkApproveRejectRequest>, BulkApproveRejectRequest>(
    async ({ ids, status }) => {
      const path = `${PATHS.bulk}/${status}`
      const res = await api.patch(path, { ids })

      return res.data
    },
    {
      mutationKey: [RequestsKeys.ApproveReject],
      onSuccess: () => {
        cache.invalidateQueries([RequestsKeys.Requests])
        cache.removeQueries({ queryKey: [AccountingKeys.Contacts], type: 'inactive' })
      },
    },
  )
}

export const usePublicComment = (uuid: string) => {
  return useCreate<{ text: string }>(`${PATHS.requests}p/${uuid}/comment`)
}

export const useGetPublicInfo = (uuid: string) => {
  return useGetOne<
    {
      id: number
      status: string
      actions: { reject: string; approve: string }
    },
    {
      id: number
      status: string
    }
  >([RequestsKeys.PublicRequest], `${PATHS.requests}p/${uuid}`)
}

export const useCreateRequest = () => {
  return useCreate<RequestPayload, ApprovalRequest>(PATHS.requests)
}

export const useUpdateRequest = (requestId?: string | number) => {
  const cache = useQueryClient()
  return usePatch<RequestPayload, ApprovalRequest>(`${PATHS.requests}${requestId}`, {
    mutation: {
      onSuccess: () => {
        cache.removeQueries([RequestsKeys.Request, requestId])
      },
    },
  })
}
export const useSendScheduledRequestNow = (requestId: string | number) => {
  const cache = useQueryClient()
  return usePatch(PATHS.sendNow.replace(':id', requestId?.toString()), {
    mutation: {
      onSuccess: () => {
        cache.invalidateQueries([RequestsKeys.Request, requestId])
      },
    },
  })
}

export const useUpdateRequestSteps = (id: string | number) => {
  const cache = useQueryClient()
  return usePatch<RequestStepUpdateRequest>(`${PATHS.requests}${id}/approvers`, {
    mutation: {
      onSuccess: () => {
        cache.invalidateQueries([RequestsKeys.Request, id])
      },
    },
  })
}

export const useExport = (params: ExportFilter) => {
  return useMutation(
    async () => {
      const response = await api.get(PATHS.export, {
        params: Object.keys(params).reduce(
          (acc, key) =>
            params[key as keyof ExportFilter] ? { ...acc, [key]: params[key as keyof ExportFilter] } : acc,
          {},
        ),
        responseType: 'blob',
      })

      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `approval_requests_${new Date().toISOString()}.${params.type}`)
      document.body.appendChild(link)
      link.click()

      return null
    },
    {
      mutationKey: [RequestsKeys.Export],
    },
  )
}

export const useSendComment = (id?: string | number) => {
  const cache = useQueryClient()
  return useCreate<CommentRequest>(generatePath(PATHS.comments, { id }), {
    mutation: {
      onSuccess: () => {
        cache.invalidateQueries([RequestsKeys.RequestComments, id])
      },
    },
    query: {
      enabled: !!id,
    },
  })
}

export const useGetComments = (id?: string | number) => {
  return useGetList<Comment>([RequestsKeys.RequestComments, id], generatePath(PATHS.comments, { id }), {
    query: {
      enabled: !!id,
    },
  })
}

export const useRemindApprovers = (id: string | number) => {
  const cache = useQueryClient()
  return usePatch(generatePath(PATHS.remind, { id }), {
    mutation: {
      onSuccess: () => {
        cache.invalidateQueries([RequestsKeys.Request, id])
      },
    },
  })
}

export interface ForwardRecipient {
  id: number
  full_name: string
  email: string
}

export interface ForwardRecipientsResponse {
  recipients: ForwardRecipient[]
}

export const useGetForwardRecipients = (id?: string | number) => {
  return useGetOne<ForwardRecipientsResponse>(
    [RequestsKeys.ForwardRecipients, id],
    generatePath(PATHS.forward, { id }),
    {
      query: {
        enabled: !!id,
      },
    },
  )
}

export interface ForwardRequestPayload {
  recipients: number[]
  message?: string
}

export interface ForwardRequestResponse {
  id: string
  message: string
  recipients_count: number
}

export const useForwardRequest = (id: string | number) => {
  const cache = useQueryClient()
  return useMutation<ForwardRequestResponse, AxiosError<{ error: string }>, ForwardRequestPayload>(
    async (payload: ForwardRequestPayload) => {
      const path = generatePath(PATHS.forward, { id })
      const res = await api.post<ForwardRequestResponse>(path, payload)
      return res.data
    },
    {
      mutationKey: [RequestsKeys.ForwardRequest],
      onSuccess: () => {
        cache.invalidateQueries([RequestsKeys.Request, id])
      },
    },
  )
}
